/**
 * Conversão de quantidades entre unidades de um produto, SEM ponto flutuante.
 *
 * Cada embalagem tem um fator = quantas unidades-base cabem nela
 * (ex.: base KG; PCT 500 g → fator 0,5; FD com 10 PCT → fator 5).
 * Quantidade na base = quantidade × fator. Tudo em decimal com 6 casas,
 * a mesma precisão do banco (numeric(18,6)).
 */

export const QUANTITY_SCALE = 6;
const SCALE = 10n ** BigInt(QUANTITY_SCALE);

/** Converte "1.5", "1,5", 1.5 em inteiro escalado (×10^6). Lança erro se inválido. */
export function toScaled(value: string | number): bigint {
  const s = String(value).trim().replace(",", ".");
  const m = /^(-)?(\d+)(?:\.(\d+))?$/.exec(s);
  if (!m) throw new Error(`Quantidade inválida: ${value}`);
  const [, sign, int, frac = ""] = m;
  if (frac.length > QUANTITY_SCALE && /[1-9]/.test(frac.slice(QUANTITY_SCALE))) {
    throw new Error(`Máximo de ${QUANTITY_SCALE} casas decimais: ${value}`);
  }
  const scaled =
    BigInt(int!) * SCALE + BigInt(frac.slice(0, QUANTITY_SCALE).padEnd(QUANTITY_SCALE, "0"));
  return sign ? -scaled : scaled;
}

/** Inteiro escalado → texto decimal sem zeros à direita ("5", "0.5", "12.345"). */
export function fromScaled(value: bigint): string {
  const neg = value < 0n;
  const abs = neg ? -value : value;
  const int = abs / SCALE;
  const frac = (abs % SCALE).toString().padStart(QUANTITY_SCALE, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${int}${frac ? `.${frac}` : ""}`;
}

/** Multiplica dois decimais e arredonda para 6 casas (meio para cima, como o numeric do PostgreSQL). */
export function multiplyDecimal(a: string | number, b: string | number): string {
  const product = toScaled(a) * toScaled(b); // escala 10^12
  const neg = product < 0n;
  const abs = neg ? -product : product;
  const rounded = (abs + SCALE / 2n) / SCALE;
  return fromScaled(neg ? -rounded : rounded);
}

/** Quantidade na unidade-base: quantidade × fator da unidade. */
export function convertToBase(quantity: string | number, factor: string | number): string {
  return multiplyDecimal(quantity, factor);
}

/**
 * Fator de uma embalagem descrita pela composição:
 * "1 FD contém 10 PCT" e PCT tem fator 0,5 → 10 × 0,5 = 5.
 */
export function packagingFactor(
  containsQuantity: string | number,
  innerFactor: string | number,
): string {
  return multiplyDecimal(containsQuantity, innerFactor);
}

/** Compara dois decimais: -1, 0 ou 1. */
export function compareDecimal(a: string | number, b: string | number): number {
  const x = toScaled(a);
  const y = toScaled(b);
  return x < y ? -1 : x > y ? 1 : 0;
}

/** Texto amigável de uma quantidade no padrão brasileiro ("5", "0,5", "1.250,75"). */
export function formatQuantity(value: string | number, maxDecimals = QUANTITY_SCALE): string {
  const [int, frac = ""] = fromScaled(toScaled(value)).split(".");
  const intFmt = int!.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const f = frac.slice(0, maxDecimals).replace(/0+$/, "");
  return f ? `${intFmt},${f}` : intFmt;
}
